import React, { useMemo } from "react"
import InputText from "../inputText/inputText"
import styles from "./hotelRates.module.scss"
import { HotelRoomTypeModel, MealPlanTypeModel, RoomSpecificDifferentSurchargeModel } from "../../models"
import { toTitleCase } from "util/textcase"
import IconsColor from "../../util/iconsColor"

type SurchargeMode = "VND" | "%"

const modeFromFlag = (isPercentage?: boolean | null): SurchargeMode => (isPercentage === true ? "%" : "VND")
const flagFromMode = (mode: SurchargeMode): boolean => mode === "%"

const ModeToggle: React.FC<{
    value: SurchargeMode
    onChange: (mode: SurchargeMode) => void
    disabled?: boolean
}> = ({ value, onChange, disabled }) => (
    <div className={styles.modeSelectWrapper}>
        <select
            className={styles.modeSelect}
            value={value}
            onChange={(e) => onChange(e.target.value as SurchargeMode)}
            disabled={disabled}
            aria-label="Surcharge unit"
        >
            <option value="VND">VND</option>
            <option value="%">%</option>
        </select>
        <span className={styles.modeSelectChevron}>
            <IconsColor iconName="chevronDown" width="14px" height="14px" fillColor="none" strokeColor="currentColor" />
        </span>
    </div>
)

type DayWiseSurchargeRow = {
    rowKey: string
    label: string
    amount: number | null
    isPercentage?: boolean
    groupId: string | null
    dayWisePricingGroupSurcharge: {
        flag_different_surcharge: boolean
        surcharge_amount: number | null
        flag_surcharge_amount_is_percentage?: boolean
    }[]
}

type Props = {
    rateStructureTypeId: number
    mealPlanTypes: MealPlanTypeModel[]
    hotelRoomTypes: HotelRoomTypeModel[]
    /** Day-wise pricing groups with surcharge flag — shown under Surcharge Configuration. */
    dayWiseSurchargeRows?: DayWiseSurchargeRow[]
    onDayWiseSurchargeAmountChange?: (
        groupId: string | null,
        amount: number | null,
        flagDifferent: boolean | null,
        isPercentage?: boolean,
    ) => void

    // Case (1) fields (Simple / GIT-FIT when all room types are standard)
    surchargePerAdditionalPax: number | null
    childWithoutBedPrice: number | null
    childWithBed: number | null
    adultWithBed: number | null
    flagChildWithoutBedIsPercentage?: boolean
    flagChildWithBedIsPercentage?: boolean
    flagAdultWithBedIsPercentage?: boolean
    onChangeContractFields: (next: {
        surcharge_per_additional_pax: number | null
        child_without_bed_price: number | null
        child_with_bed: number | null
        adult_with_bed: number | null
        flag_child_without_bed_is_percentage?: boolean
        flag_child_with_bed_is_percentage?: boolean
        flag_adult_with_bed_is_percentage?: boolean
    }) => void

    // Case (2)-(4) fields
    roomSpecificDifferentSurcharge: RoomSpecificDifferentSurchargeModel[]
    onChangeRoomSpecificDifferentSurcharge: (next: RoomSpecificDifferentSurchargeModel[]) => void

    /** When true (contract `flag_seasonal_extra_bed_pricing`), hide Adult Extra Bed in surcharge tables and use per-season extra bed rates instead. */
    flagSeasonalExtraBedPricing?: boolean
    seasonalExtraBedSeasons?: { id: string; name: string }[]
    extraBedRatesBySeason?: Record<string, number | null>
    extraBedRateIsPercentageBySeason?: Record<string, boolean>
    onExtraBedRatesChange?: (next: Record<string, number | null>) => void
    onExtraBedRateIsPercentageChange?: (seasonId: string, isPercentage: boolean) => void

    readOnly?: boolean
}

const RateStructureType = {
    SIMPLE: 1,
    MEAL_PLAN: 2,
    GIT_FIT: 3,
} as const

const toPositiveNumberOrNull = (v: string): number | null => {
    const trimmed = v.trim()
    if (!trimmed) return null
    const n = parseFloat(trimmed)
    return Number.isFinite(n) && n >= 0 ? n : null
}

const preventInvalidNumberKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "-" || e.key === "e" || e.key === "E") {
        e.preventDefault()
    }
}

const PriceInput = ({
    label,
    value,
    onChange,
    mode,
    onModeChange,
    readOnly,
}: {
    label: string
    value: string | number | null | undefined
    onChange: (val: number | null) => void
    mode: SurchargeMode
    onModeChange: (mode: SurchargeMode) => void
    readOnly?: boolean
}) => {
    const handleModeChange = (newMode: SurchargeMode) => {
        if (readOnly) return
        onModeChange(newMode)
    }

    return (
        <div className={styles.surchargeFieldWrapper}>
            <InputText
                label={label}
                hideLabel={true}
                noMargin={true}
                value={value === null || value === undefined ? "" : String(value)}
                onChange={(e) => {
                    const val = toPositiveNumberOrNull(e.target.value)
                    onChange(val)
                }}
                onKeyDown={preventInvalidNumberKeys}
                type="number"
                disabled={readOnly}
                sx={{ paddingRight: "92px" }}
            />
            <div className={styles.modeToggleOverlay}>
                <ModeToggle value={mode} onChange={handleModeChange} disabled={readOnly} />
            </div>
        </div>
    )
}

const getRoomLabel = (rt: HotelRoomTypeModel) => {
    const name = (rt.hotel_room_type_name ?? "").trim()
    const bed = (rt.bed_type_name ?? "").trim()
    if (!bed) return name
    return `${name} (${bed})`
}

const isSameMealPlanId = (a: number | null | undefined, b: number | null | undefined) => {
    const aa = a == null ? null : Number(a)
    const bb = b == null ? null : Number(b)
    return aa === bb
}

const upsertRoomSpecificRow = (
    rows: RoomSpecificDifferentSurchargeModel[],
    match: { meal_plan_type_id: number | null; flag_different_surcharge: boolean },
    patch: Partial<RoomSpecificDifferentSurchargeModel>
): RoomSpecificDifferentSurchargeModel[] => {
    const idx = rows.findIndex(
        (r) =>
            isSameMealPlanId(r.meal_plan_type_id, match.meal_plan_type_id) &&
            r.flag_different_surcharge === match.flag_different_surcharge
    )

    if (idx >= 0) {
        const next = [...rows]
        next[idx] = {
            ...next[idx],
            ...patch,
            meal_plan_type_id: match.meal_plan_type_id,
            flag_different_surcharge: match.flag_different_surcharge,
        }
        return next
    }

    const nextRow: RoomSpecificDifferentSurchargeModel = {
        room_specific_different_surcharge_id: null,
        meal_plan_type_id: match.meal_plan_type_id,
        flag_different_surcharge: match.flag_different_surcharge,
        git_fit_type_id: null,
        child_with_bed_surcharge: null,
        child_without_bed_surcharge: null,
        adult_extra_bed_surcharge: null,
        flag_adult_extra_bed_surcharge_is_percentage: false,
        flag_child_with_bed_surcharge_is_percentage: false,
        flag_child_without_bed_surcharge_is_percentage: false,
        ...patch,
    }
    return [...rows, nextRow]
}

const getRow = (
    rows: RoomSpecificDifferentSurchargeModel[],
    match: { meal_plan_type_id: number | null; flag_different_surcharge: boolean }
): RoomSpecificDifferentSurchargeModel | null => {
    return (
        rows.find(
            (r) =>
                isSameMealPlanId(r.meal_plan_type_id, match.meal_plan_type_id) &&
                r.flag_different_surcharge === match.flag_different_surcharge
        ) ?? null
    )
}

const ThirdPersonSurchargeConfiguration: React.FC<Props> = ({
    rateStructureTypeId,
    mealPlanTypes,
    hotelRoomTypes,
    dayWiseSurchargeRows = [],
    onDayWiseSurchargeAmountChange,
    surchargePerAdditionalPax,
    childWithoutBedPrice,
    childWithBed,
    adultWithBed,
    flagChildWithoutBedIsPercentage = false,
    flagChildWithBedIsPercentage = false,
    flagAdultWithBedIsPercentage = false,
    onChangeContractFields,
    roomSpecificDifferentSurcharge,
    onChangeRoomSpecificDifferentSurcharge,
    flagSeasonalExtraBedPricing = false,
    seasonalExtraBedSeasons = [],
    extraBedRatesBySeason = {},
    extraBedRateIsPercentageBySeason = {},
    onExtraBedRatesChange,
    onExtraBedRateIsPercentageChange,
    readOnly = false,
}) => {
    const isMealPlan = rateStructureTypeId === RateStructureType.MEAL_PLAN

    const commitRoomSpecificSurcharge = (next: RoomSpecificDifferentSurchargeModel[]) => {
        if (flagSeasonalExtraBedPricing) {
            onChangeRoomSpecificDifferentSurcharge(
                next.map((r) => ({
                    ...r,
                    adult_extra_bed_surcharge: null,
                    flag_adult_extra_bed_surcharge_is_percentage: false,
                })),
            )
        } else {
            onChangeRoomSpecificDifferentSurcharge(next)
        }
    }
    const hasAnyDifferentSurchargeRoom = useMemo(() => {
        return hotelRoomTypes.some((rt) => rt.flag_support_different_surcharge === true)
    }, [hotelRoomTypes])

    const differentRooms = useMemo(() => {
        return hotelRoomTypes
            .filter((rt) => rt.hotel_room_type_id && rt.flag_support_different_surcharge === true)
            .map(getRoomLabel)
            .filter(Boolean)
    }, [hotelRoomTypes])

    const standardRooms = useMemo(() => {
        return hotelRoomTypes
            .filter((rt) => rt.hotel_room_type_id && (rt.flag_support_different_surcharge ?? false) === false)
            .map(getRoomLabel)
            .filter(Boolean)
    }, [hotelRoomTypes])

    const renderDayWiseSurchargeRows = (mealPlanColSpan: number, flagDifferent: boolean | null = null) => {
        if (!dayWiseSurchargeRows.length) return null
        return dayWiseSurchargeRows.map((row) => {
            let value: string = ""
            let currentAmount: number | null = null
            let isPercentage = row.isPercentage === true
            if (flagDifferent != null) {
                const dayWisePricingGroupSurcharge = Array.isArray(row.dayWisePricingGroupSurcharge)
                    ? row.dayWisePricingGroupSurcharge
                    : []
                const existingSurcharge = dayWisePricingGroupSurcharge.find(
                    (s) => s.flag_different_surcharge === flagDifferent,
                )
                if (existingSurcharge) {
                    currentAmount = existingSurcharge.surcharge_amount ?? null
                    value =
                        currentAmount === null || currentAmount === undefined
                            ? ""
                            : String(currentAmount)
                    isPercentage = existingSurcharge.flag_surcharge_amount_is_percentage === true
                } else {
                    value = ""
                    isPercentage = false
                }
            } else {
                currentAmount = row.amount
                value = row.amount === null || row.amount === undefined ? "" : String(row.amount)
            }
            return (
                <tr key={`dw-surcharge-${row.rowKey}`}>
                    <td>{toTitleCase(row.label)}</td>
                    {mealPlanColSpan <= 1 ? (
                        <td className={styles.priceCell}>
                            <PriceInput
                                label={toTitleCase(row.label)}
                                value={value}
                                mode={modeFromFlag(isPercentage)}
                                onModeChange={(mode) => {
                                    onDayWiseSurchargeAmountChange?.(
                                        row.groupId,
                                        currentAmount,
                                        flagDifferent,
                                        flagFromMode(mode),
                                    )
                                }}
                                onChange={(val) => {
                                    onDayWiseSurchargeAmountChange?.(row.groupId, val, flagDifferent)
                                }}
                                readOnly={readOnly}
                            />
                        </td>
                    ) : (
                        <td className={styles.priceCell} colSpan={mealPlanColSpan}>
                            <PriceInput
                                label={toTitleCase(row.label)}
                                value={value}
                                mode={modeFromFlag(isPercentage)}
                                onModeChange={(mode) => {
                                    onDayWiseSurchargeAmountChange?.(
                                        row.groupId,
                                        currentAmount,
                                        flagDifferent,
                                        flagFromMode(mode),
                                    )
                                }}
                                onChange={(val) => {
                                    onDayWiseSurchargeAmountChange?.(row.groupId, val, flagDifferent)
                                }}
                                readOnly={readOnly}
                            />
                        </td>
                    )}
                </tr>
            )
        })
    }

    const renderRoomPills = (labels: string[]) => {
        if (!labels || labels.length === 0) return null
        return (
            <div className={styles.roomChipsRow}>
                {labels.map((name) => (
                    <div key={name} className={styles.roomChip}>
                        {toTitleCase(name)}
                    </div>
                ))}
            </div>
        )
    }

    const renderSimpleStandardTable = () => {
        return (
            <div className={styles.surchargeTableContainer}>
                <table className={styles.surchargeTable}>
                    <thead>
                        <tr>
                            <th>Surcharge Type</th>
                            <th>Amount (VND/Night)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Child Without Bed</td>
                            <td className={styles.priceCell}>
                                <PriceInput
                                    label="Child Without Bed"
                                    value={childWithoutBedPrice}
                                    mode={modeFromFlag(flagChildWithoutBedIsPercentage)}
                                    onModeChange={(mode) => {
                                        onChangeContractFields({
                                            surcharge_per_additional_pax: surchargePerAdditionalPax,
                                            child_without_bed_price: childWithoutBedPrice,
                                            child_with_bed: childWithBed,
                                            adult_with_bed: flagSeasonalExtraBedPricing ? null : adultWithBed,
                                            flag_child_without_bed_is_percentage: flagFromMode(mode),
                                        })
                                    }}
                                    onChange={(val) => {
                                        onChangeContractFields({
                                            surcharge_per_additional_pax: surchargePerAdditionalPax,
                                            child_without_bed_price: val,
                                            child_with_bed: childWithBed,
                                            adult_with_bed: flagSeasonalExtraBedPricing ? null : adultWithBed,
                                        })
                                    }}
                                    readOnly={readOnly}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>Child With Bed</td>
                            <td className={styles.priceCell}>
                                <PriceInput
                                    label="Child With Bed"
                                    value={childWithBed}
                                    mode={modeFromFlag(flagChildWithBedIsPercentage)}
                                    onModeChange={(mode) => {
                                        onChangeContractFields({
                                            surcharge_per_additional_pax: surchargePerAdditionalPax,
                                            child_without_bed_price: childWithoutBedPrice,
                                            child_with_bed: childWithBed,
                                            adult_with_bed: flagSeasonalExtraBedPricing ? null : adultWithBed,
                                            flag_child_with_bed_is_percentage: flagFromMode(mode),
                                        })
                                    }}
                                    onChange={(val) => {
                                        onChangeContractFields({
                                            surcharge_per_additional_pax: surchargePerAdditionalPax,
                                            child_without_bed_price: childWithoutBedPrice,
                                            child_with_bed: val,
                                            adult_with_bed: flagSeasonalExtraBedPricing ? null : adultWithBed,
                                        })
                                    }}
                                    readOnly={readOnly}
                                />
                            </td>
                        </tr>
                        {!flagSeasonalExtraBedPricing && (
                            <tr>
                                <td>Adult Extra Bed</td>
                                <td className={styles.priceCell}>
                                    <PriceInput
                                        label="Adult Extra Bed"
                                        value={adultWithBed}
                                        mode={modeFromFlag(flagAdultWithBedIsPercentage)}
                                        onModeChange={(mode) => {
                                            onChangeContractFields({
                                                surcharge_per_additional_pax: surchargePerAdditionalPax,
                                                child_without_bed_price: childWithoutBedPrice,
                                                child_with_bed: childWithBed,
                                                adult_with_bed: adultWithBed,
                                                flag_adult_with_bed_is_percentage: flagFromMode(mode),
                                            })
                                        }}
                                        onChange={(val) => {
                                            onChangeContractFields({
                                                surcharge_per_additional_pax: surchargePerAdditionalPax,
                                                child_without_bed_price: childWithoutBedPrice,
                                                child_with_bed: childWithBed,
                                                adult_with_bed: val,
                                            })
                                        }}
                                        readOnly={readOnly}
                                    />
                                </td>
                            </tr>
                        )}
                        {renderDayWiseSurchargeRows(1, null)}
                    </tbody>
                </table>
            </div>
        )
    }

    const renderSimpleRoomSpecificTable = (flagDifferent: boolean, title: string, roomLabels: string[]) => {
        const row = getRow(roomSpecificDifferentSurcharge, { meal_plan_type_id: null, flag_different_surcharge: flagDifferent })
        const cwb = row?.child_with_bed_surcharge ?? null
        const cwob = row?.child_without_bed_surcharge ?? null
        const aeb = row?.adult_extra_bed_surcharge ?? null
        const match = { meal_plan_type_id: null as number | null, flag_different_surcharge: flagDifferent }

        return (
            <div className={styles.surchargeGroupCard}>
                <div className={styles.surchargeGroupHeader}>
                    <div className={styles.surchargeGroupTitle}>{title}</div>
                </div>
                <div className={styles.surchargeGroupBody}>
                    {renderRoomPills(roomLabels)}
                    <div className={styles.surchargeTableContainer}>
                        <table className={styles.surchargeTable}>
                            <thead>
                                <tr>
                                    <th>Surcharge Type</th>
                                    <th>Amount (VND/Night)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Child Without Bed</td>
                                    <td className={styles.priceCell}>
                                        <PriceInput
                                            label="Child Without Bed"
                                            value={cwob}
                                            mode={modeFromFlag(row?.flag_child_without_bed_surcharge_is_percentage)}
                                            onModeChange={(mode) => {
                                                commitRoomSpecificSurcharge(
                                                    upsertRoomSpecificRow(roomSpecificDifferentSurcharge, match, {
                                                        child_without_bed_surcharge: cwob,
                                                        flag_child_without_bed_surcharge_is_percentage: flagFromMode(mode),
                                                    }),
                                                )
                                            }}
                                            onChange={(val) => {
                                                commitRoomSpecificSurcharge(
                                                    upsertRoomSpecificRow(roomSpecificDifferentSurcharge, match, {
                                                        child_without_bed_surcharge: val,
                                                    }),
                                                )
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td>Child With Bed</td>
                                    <td className={styles.priceCell}>
                                        <PriceInput
                                            label="Child With Bed"
                                            value={cwb}
                                            mode={modeFromFlag(row?.flag_child_with_bed_surcharge_is_percentage)}
                                            onModeChange={(mode) => {
                                                commitRoomSpecificSurcharge(
                                                    upsertRoomSpecificRow(roomSpecificDifferentSurcharge, match, {
                                                        child_with_bed_surcharge: cwb,
                                                        flag_child_with_bed_surcharge_is_percentage: flagFromMode(mode),
                                                    }),
                                                )
                                            }}
                                            onChange={(val) => {
                                                commitRoomSpecificSurcharge(
                                                    upsertRoomSpecificRow(roomSpecificDifferentSurcharge, match, {
                                                        child_with_bed_surcharge: val,
                                                    }),
                                                )
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </td>
                                </tr>
                                {!flagSeasonalExtraBedPricing && (
                                    <tr>
                                        <td>Adult Extra Bed</td>
                                        <td className={styles.priceCell}>
                                            <PriceInput
                                                label="Adult Extra Bed"
                                                value={aeb}
                                                mode={modeFromFlag(row?.flag_adult_extra_bed_surcharge_is_percentage)}
                                                onModeChange={(mode) => {
                                                    commitRoomSpecificSurcharge(
                                                        upsertRoomSpecificRow(roomSpecificDifferentSurcharge, match, {
                                                            adult_extra_bed_surcharge: aeb,
                                                            flag_adult_extra_bed_surcharge_is_percentage: flagFromMode(mode),
                                                        }),
                                                    )
                                                }}
                                                onChange={(val) => {
                                                    commitRoomSpecificSurcharge(
                                                        upsertRoomSpecificRow(roomSpecificDifferentSurcharge, match, {
                                                            adult_extra_bed_surcharge: val,
                                                        }),
                                                    )
                                                }}
                                                readOnly={readOnly}
                                            />
                                        </td>
                                    </tr>
                                )}
                                {renderDayWiseSurchargeRows(1, flagDifferent)}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )
    }

    const renderMealPlanTable = (flagDifferent: boolean, title: string, roomLabels: string[]) => {
        const hasMealPlans = mealPlanTypes && mealPlanTypes.length > 0
        if (!hasMealPlans) return null

        const getVal = (mealPlanTypeId: number, key: "child_without_bed_surcharge" | "child_with_bed_surcharge" | "adult_extra_bed_surcharge") => {
            const row = getRow(roomSpecificDifferentSurcharge, { meal_plan_type_id: mealPlanTypeId, flag_different_surcharge: flagDifferent })
            return (row?.[key] ?? null) as number | null
        }

        const getFlag = (
            mealPlanTypeId: number,
            key:
                | "flag_child_without_bed_surcharge_is_percentage"
                | "flag_child_with_bed_surcharge_is_percentage"
                | "flag_adult_extra_bed_surcharge_is_percentage",
        ) => {
            const row = getRow(roomSpecificDifferentSurcharge, { meal_plan_type_id: mealPlanTypeId, flag_different_surcharge: flagDifferent })
            return row?.[key] === true
        }

        const setVal = (
            mealPlanTypeId: number,
            patch: Partial<RoomSpecificDifferentSurchargeModel>
        ) => {
            const next = upsertRoomSpecificRow(roomSpecificDifferentSurcharge, { meal_plan_type_id: mealPlanTypeId, flag_different_surcharge: flagDifferent }, patch)
            commitRoomSpecificSurcharge(next)
        }
        return (
            <div className={styles.surchargeGroupCard}>
                <div className={title === "" ? "" : styles.surchargeGroupHeader}>
                    <div className={styles.surchargeGroupTitle}>{toTitleCase(title)}</div>
                </div>
                <div className={styles.surchargeGroupBody}>
                    {renderRoomPills(roomLabels)}
                    <div className={styles.extraBedTableContainer}>
                        <table className={styles.extraBedTable}>
                            <thead>
                                <tr>
                                    <th>Surcharge Type</th>
                                    {mealPlanTypes.map((mp) => (
                                        <th key={mp.meal_plan_type_id}>{toTitleCase(mp.meal_plan_type_name)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Child Without Bed</td>
                                    {mealPlanTypes.map((mp) => {
                                        const v = getVal(mp.meal_plan_type_id, "child_without_bed_surcharge")
                                        return (
                                            <td key={mp.meal_plan_type_id} className={styles.priceCell}>
                                                <PriceInput
                                                    label={`Child Without Bed - ${mp.meal_plan_type_name}`}
                                                    value={v}
                                                    mode={modeFromFlag(getFlag(mp.meal_plan_type_id, "flag_child_without_bed_surcharge_is_percentage"))}
                                                    onModeChange={(mode) => {
                                                        setVal(mp.meal_plan_type_id, {
                                                            child_without_bed_surcharge: v,
                                                            flag_child_without_bed_surcharge_is_percentage: flagFromMode(mode),
                                                        })
                                                    }}
                                                    onChange={(val) => {
                                                        setVal(mp.meal_plan_type_id, { child_without_bed_surcharge: val })
                                                    }}
                                                    readOnly={readOnly}
                                                />
                                            </td>
                                        )
                                    })}
                                </tr>
                                <tr>
                                    <td>Child With Bed</td>
                                    {mealPlanTypes.map((mp) => {
                                        const v = getVal(mp.meal_plan_type_id, "child_with_bed_surcharge")
                                        return (
                                            <td key={mp.meal_plan_type_id} className={styles.priceCell}>
                                                <PriceInput
                                                    label={`Child With Bed - ${mp.meal_plan_type_name}`}
                                                    value={v}
                                                    mode={modeFromFlag(getFlag(mp.meal_plan_type_id, "flag_child_with_bed_surcharge_is_percentage"))}
                                                    onModeChange={(mode) => {
                                                        setVal(mp.meal_plan_type_id, {
                                                            child_with_bed_surcharge: v,
                                                            flag_child_with_bed_surcharge_is_percentage: flagFromMode(mode),
                                                        })
                                                    }}
                                                    onChange={(val) => {
                                                        setVal(mp.meal_plan_type_id, { child_with_bed_surcharge: val })
                                                    }}
                                                    readOnly={readOnly}
                                                />
                                            </td>
                                        )
                                    })}
                                </tr>
                                {!flagSeasonalExtraBedPricing && (
                                    <tr>
                                        <td>Adult Extra Bed</td>
                                        {mealPlanTypes.map((mp) => {
                                            const v = getVal(mp.meal_plan_type_id, "adult_extra_bed_surcharge")
                                            return (
                                                <td key={mp.meal_plan_type_id} className={styles.priceCell}>
                                                    <PriceInput
                                                        label={`Adult Extra Bed - ${mp.meal_plan_type_name}`}
                                                        value={v}
                                                        mode={modeFromFlag(getFlag(mp.meal_plan_type_id, "flag_adult_extra_bed_surcharge_is_percentage"))}
                                                        onModeChange={(mode) => {
                                                            setVal(mp.meal_plan_type_id, {
                                                                adult_extra_bed_surcharge: v,
                                                                flag_adult_extra_bed_surcharge_is_percentage: flagFromMode(mode),
                                                            })
                                                        }}
                                                        onChange={(val) => {
                                                            setVal(mp.meal_plan_type_id, { adult_extra_bed_surcharge: val })
                                                        }}
                                                        readOnly={readOnly}
                                                    />
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )}
                                {renderDayWiseSurchargeRows(mealPlanTypes.length)}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )
    }

    const renderSeasonalExtraBedBySeason = () => {
        if (!flagSeasonalExtraBedPricing || !seasonalExtraBedSeasons.length) return null
        return (
            <div className={styles.seasonalExtraBedBlock}>
                <div className={styles.seasonalExtraBedTableTitle}>Extra Bed Rate</div>
                <div className={styles.seasonalExtraBedTableWrap}>
                    <table className={styles.seasonalExtraBedRateTable}>
                        <thead>
                            <tr>
                                <th>Season</th>
                                <th>Extra Bed Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {seasonalExtraBedSeasons.map((s) => (
                                <tr key={s.id}>
                                    <td>{toTitleCase(s.name)}</td>
                                    <td className={styles.priceCell}>
                                        <PriceInput
                                            label={`Extra bed rate - ${s.name}`}
                                            value={extraBedRatesBySeason[s.id]}
                                            mode={modeFromFlag(extraBedRateIsPercentageBySeason[s.id])}
                                            onModeChange={(mode) => {
                                                onExtraBedRateIsPercentageChange?.(s.id, flagFromMode(mode))
                                            }}
                                            onChange={(val) => {
                                                onExtraBedRatesChange?.({
                                                    ...extraBedRatesBySeason,
                                                    [s.id]: val,
                                                })
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.peakRoomSurchargeCard}>
            <div className={styles.peakRoomSurchargeHeader}>
                <div>
                    <div className={styles.peakRoomSurchargeTitle}>Surcharge Configuration</div>
                    <div className={styles.peakRoomSurchargeSubTitle}>Additional Charges Per Room Per Night In VND</div>
                </div>
            </div>

            <div className={styles.peakRoomSurchargeBody}>
                {/* Simple / GIT-FIT */}
                {!isMealPlan && !hasAnyDifferentSurchargeRoom && <>{renderSimpleStandardTable()}</>}

                {!isMealPlan && hasAnyDifferentSurchargeRoom && (
                    <>
                        {differentRooms.length > 0 && standardRooms.length > 0 ? (
                            <div className={styles.twoTableRow}>
                                {renderSimpleRoomSpecificTable(true, "Room-Specific 3rd Person Surcharges (Different Rates)", differentRooms)}
                                {renderSimpleRoomSpecificTable(false, "Standard 3rd Person Surcharges", standardRooms)}
                            </div>
                        ) : (
                            <>
                                {differentRooms.length > 0 &&
                                    renderSimpleRoomSpecificTable(true, "Room-Specific 3rd Person Surcharges (Different Rates)", differentRooms)}
                                {standardRooms.length > 0 &&
                                    renderSimpleRoomSpecificTable(false, "Standard 3rd Person Surcharges", standardRooms)}
                            </>
                        )}
                    </>
                )}

                {/* Seasonal Extra Bed */}
                {renderSeasonalExtraBedBySeason()}

                {/* Meal Plan */}
                {isMealPlan && !hasAnyDifferentSurchargeRoom && (
                    <>
                        {renderMealPlanTable(false, "", [])}
                    </>
                )}

                {isMealPlan && hasAnyDifferentSurchargeRoom && (
                    <>
                        {differentRooms.length > 0 && standardRooms.length > 0 ? (
                            <div className={styles.twoTableRow}>
                                {renderMealPlanTable(true, "Room-Specific 3rd Person Surcharges (Different Rates)", differentRooms)}
                                {renderMealPlanTable(false, "Standard 3rd Person Surcharges", standardRooms)}
                            </div>
                        ) : (
                            <>
                                {differentRooms.length > 0 && renderMealPlanTable(true, "Room-Specific 3rd Person Surcharges (Different Rates)", differentRooms)}
                                {standardRooms.length > 0 && renderMealPlanTable(false, "Standard 3rd Person Surcharges", standardRooms)}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default ThirdPersonSurchargeConfiguration
